import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useMemo } from "react";
import { Pie } from "react-chartjs-2";
import { mapItemType, mapItemTypeColor } from "../HelperFunctions";
import { Card } from "react-bootstrap";
import LoadingIndicator from "../LoadingIndicator";
import ErrorResponse from "../ErrorResponse";

ChartJS.register(ArcElement, Tooltip, Legend, annotationPlugin, ChartDataLabels);

export default function ClocksContentBalanceCard({ data, avgData, dataLoading, avgDataLoading, error }) {
    const chartData = useMemo(() => {
        if (data) {
            return {
                labels: data.balance.map(x => {
                    // Check if the item type is 'Commercial' and change it to 'Ads'
                    const itemTypeLabel = mapItemType(x.item_type) === "Commercial" ? "Ads" : mapItemType(x.item_type);

                    const avgItem = avgData?.balance?.find(avg => avg.item_type === x.item_type);
                    const avgPercentage = avgItem ? Math.round(avgItem.percentage) : "--";
                    return `${itemTypeLabel} (avg. ${avgPercentage}%)`;
                }),
                datasets: [
                    {
                        type: "pie",
                        backgroundColor: data.balance.map(x => mapItemTypeColor(x.item_type)),
                        data: data.balance.map(x => x.percentage),
                        datalabels: {
                            anchor: "end",
                        },
                    },
                ],
            };
        }
    }, [data, avgData]);

    const options = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            layout: {
                padding: 8,
            },
            plugins: {
                legend: {
                    display: true,
                    labels: {
                        usePointStyle: true,
                    },
                    position: "right",
                },
                tooltip: {
                    enabled: true,
                    displayColors: false,
                    callbacks: {
                        label: function (tooltipItem) {
                            const index = tooltipItem.dataIndex;
                            const itemType = data.balance[index]?.item_type;

                            // If item_type is 'Commercial', change it to 'Ads' in tooltip
                            const itemTypeLabel = mapItemType(itemType) === "Commercial" ? "Ads" : mapItemType(itemType);
                            const currentPercentage = Math.round(tooltipItem.parsed);

                            const avg = avgData?.balance?.find(x => x.item_type === itemType);
                            const avgPercentage = avg ? Math.round(avg.percentage) : "--";

                            return `${itemTypeLabel} ${currentPercentage}% (avg. ${avgPercentage}%)`;
                        },
                        title: function () {
                            return "";
                        },
                    },
                },

                datalabels: {
                    backgroundColor: function (context) {
                        return context.dataset.backgroundColor;
                    },
                    borderColor: "white",
                    borderRadius: 5,
                    borderWidth: 2,
                    color: "white",
                    font: {
                        weight: "bold",
                        size: 12,
                    },
                    padding: 4,
                    formatter: function (value) {
                        return `${Math.round(value)}%`; // Only show the current value in the datalabel
                    },
                },
            },
            interaction: {
                mode: "index",
            },
        }),
        [avgData?.balance, data?.balance]
    );

    if (!data && !avgData && !dataLoading && !avgDataLoading && !error) return null;

    return (
        <Card className="">
            <Card.Header className="fs-7">Content Balance</Card.Header>
            <Card.Body className="p-1">
                {dataLoading && (
                    <div className="p-4">
                        <LoadingIndicator width={72} />
                    </div>
                )}
                {!dataLoading && error && <ErrorResponse errorCode={error} />}
                {!dataLoading && data && (
                    <div style={{ width: 300 }}>
                        <Pie data={chartData} options={options} />
                    </div>
                )}
            </Card.Body>
        </Card>
    );
}
